// apps/provider/src/stores/scribeStore.js

import { create } from 'zustand';
import { scribeRecordingService } from '../services/scribeRecordingService';
import { scribeEncounterService } from '../services/scribeEncounterService';
import { aiService } from '../services/aiService';
import { apiService } from '../services/api';

const buildRecordingState = () => {
  const recording = scribeEncounterService.currentRecording;
  if (!recording) return null;
  return {
    backendId: recording.backendId,
    duration: scribeEncounterService.getTotalRecordingDuration(),
    transcriptionStatus: recording.transcriptionStatus || 'pending',
    transcriptionError: recording.transcriptionError || null,
    hasAudio: scribeEncounterService.hasAudio(),
    segmentCount: recording.segments?.length || 0,
  };
};

export const useScribeStore = create((set, get) => ({
  // Recording state
  isRecording: false,
  isPaused: false,
  recordingDuration: 0,
  recordingStartTime: null,
  wakeLockActive: false,

  // Encounter service state
  encounterInitialized: false,
  recordingId: null,

  // Existing recording state (hydrated from backend)
  existingRecordingState: null,  // { backendId, duration, transcriptionStatus, hasAudio, segmentCount }

  // Template state
  selectedTemplate: null,
  templates: [],
  isLoadingTemplates: false,
  templateError: null,

  // Context set by ClinicalNoteEdit when editing a template-driven note
  activeNoteContext: null,

  // Transcription state
  isTranscribing: false,
  transcript: '',
  transcriptionError: null,

  // SOAP note generation state
  isGeneratingNote: false,
  generatedNote: null,
  noteGenerationError: null,

  // Template-mode processing state
  isProcessing: false,
  processingStatus: null,
  generatedSectionsData: null,

  // UI state
  isMinimized: false,
  showTranscript: false,
  showSettings: false,

  // Settings
  settings: {
    autoTranscribe: true,
    detectSilence: true,
    language: 'en',
    streamingTranscription: false,
  },

  // ============ Template Actions ============

  loadTemplates: async (organizationId, userId) => {
    if (get().templates.length > 0 || get().isLoadingTemplates) return;
    set({ isLoadingTemplates: true, templateError: null });
    try {
      const templates = await apiService.getNoteTemplates(organizationId, userId);
      set({ templates: templates || [], isLoadingTemplates: false });
    } catch (error) {
      set({ templateError: error.message, isLoadingTemplates: false });
    }
  },

  selectTemplate: (template) => {
    set({ selectedTemplate: template });
  },

  setActiveNoteContext: (context) => {
    // If a recording session is active, preserve its state across note navigation
    if (scribeRecordingService.isRecording) {
      set({ activeNoteContext: context });
      return;
    }

    set({
      activeNoteContext: context,
      existingRecordingState: null,
      encounterInitialized: false,
      recordingId: null,
      transcript: '',
      isProcessing: false,
      processingStatus: null,
    });
    if (context?.template) {
      set({ selectedTemplate: context.template });
    }
  },

  // ============ Encounter Initialization ============

  initializeEncounter: async (encounterId) => {
    if (scribeRecordingService.isRecording) {
      return;
    }
    try {
      await scribeEncounterService.initializeForEncounter(encounterId);
      set({
        encounterInitialized: true,
        recordingId: scribeEncounterService.currentRecording?.backendId || null,
      });
    } catch (error) {
      console.error('[ScribeStore] Failed to initialize encounter:', error);
      throw error;
    }
  },

  /**
   * Load existing recording state for an encounter without starting a new recording.
   * Called when the scribe panel opens to hydrate persisted state.
   */
  loadExistingRecording: async (encounterId) => {
    if (scribeRecordingService.isRecording) {
      return;
    }
    try {
      await scribeEncounterService.initializeForEncounter(encounterId);
      const recording = scribeEncounterService.currentRecording;

      set({
        encounterInitialized: true,
        recordingId: recording?.backendId || null,
        transcript: recording?.transcript || scribeEncounterService.aggregatedTranscript || '',
        existingRecordingState: recording ? {
          backendId: recording.backendId,
          duration: recording.duration || 0,
          transcriptionStatus: recording.transcriptionStatus || 'pending',
          transcriptionError: recording.transcriptionError || null,
          hasAudio: scribeEncounterService.hasAudio(),
          segmentCount: recording.segments?.length || 0,
        } : null,
      });
    } catch (error) {
      console.error('[ScribeStore] Failed to load existing recording:', error);
    }
  },

  // ============ Recording Actions (using scribeRecordingService) ============

  hasActiveRecordingForOtherEncounter: (encounterId) => {
    return scribeRecordingService.isRecording &&
      scribeEncounterService.currentRecording &&
      String(scribeEncounterService.currentRecording.encounterId) !== String(encounterId);
  },

  startRecording: async () => {
    if (get().isRecordingLocked()) {
      return { success: false, error: 'Recording is locked — transcription already submitted' };
    }

    const { encounterInitialized, activeNoteContext, selectedTemplate } = get();
    const isTemplateMode = selectedTemplate && selectedTemplate.id !== '__soap__';

    // Guard: don't clobber a recording for a different encounter
    if (activeNoteContext?.encounterId && get().hasActiveRecordingForOtherEncounter(activeNoteContext.encounterId)) {
      return { success: false, error: 'RECORDING_IN_PROGRESS_OTHER_NOTE' };
    }

    if (isTemplateMode) {
      if (!encounterInitialized && activeNoteContext?.encounterId) {
        try {
          await scribeEncounterService.initializeForEncounter(activeNoteContext.encounterId);
          set({ encounterInitialized: true });
        } catch (error) {
          return { success: false, error: 'Failed to initialize encounter: ' + error.message };
        }
      }

      if (!get().encounterInitialized) {
        return { success: false, error: 'Open a clinical note to use template mode' };
      }
    }

    if (!isTemplateMode && !get().encounterInitialized) {
      if (activeNoteContext?.encounterId) {
        try {
          await scribeEncounterService.initializeForEncounter(activeNoteContext.encounterId);
          set({ encounterInitialized: true });
        } catch (error) {
          return { success: false, error: 'Failed to initialize encounter: ' + error.message };
        }
      } else {
        return { success: false, error: 'NO_ENCOUNTER_CONTEXT' };
      }
    }

    const statusCallback = (status) => {
      set({ recordingDuration: status.duration });
    };

    const result = await scribeRecordingService.startRecording(statusCallback);

    if (result.success) {
      set({
        isRecording: true,
        isPaused: false,
        recordingStartTime: Date.now(),
        recordingDuration: 0,
        wakeLockActive: result.wakeLockActive || false,
        transcript: '',
        transcriptionError: null,
        generatedNote: null,
        generatedSectionsData: null,
        noteGenerationError: null,
      });
    }

    return result;
  },

  stopRecording: async () => {
    const result = await scribeRecordingService.stopRecording();

    set({
      isRecording: false,
      isPaused: false,
      wakeLockActive: false,
    });

    const recording = scribeEncounterService.currentRecording;
    if (recording) {
      set({
        recordingId: recording.backendId || null,
        existingRecordingState: buildRecordingState(),
      });
    }

    return result;
  },

  pauseRecording: async () => {
    if (!get().isRecording || get().isPaused) {
      return { success: false, error: 'Not recording' };
    }

    set({ isPaused: true });

    const result = await scribeRecordingService.pauseRecording();

    set({ isPaused: scribeRecordingService.isPaused });

    if (result.success) {
      set({
        recordingId: scribeEncounterService.currentRecording?.backendId || null,
        existingRecordingState: buildRecordingState(),
      });
    }

    return result;
  },

  resumeRecording: async () => {
    const result = await scribeRecordingService.resumeRecording();
    if (result.success) {
      set({ isPaused: false });
    }
    set({ recordingId: scribeEncounterService.currentRecording?.backendId || null });
    return result;
  },

  // ============ Helpers ============

  /** Check if there is recorded audio available (via encounter service) */
  hasRecordedAudio: () => {
    const status = scribeEncounterService.getRecordingStatus();
    return status?.hasAudio || false;
  },

  /** Recording is locked when transcription has completed or is in progress.
   *  'transcribed' = transcript saved, documentation generating (still busy).
   *  'failed' is NOT locked — the user can retry transcription. */
  isRecordingLocked: () => {
    const state = get().existingRecordingState;
    return state?.transcriptionStatus === 'completed'
      || state?.transcriptionStatus === 'processing'
      || state?.transcriptionStatus === 'transcribed';
  },

  /** Whether documentation has been populated (transcription pipeline ran to completion) */
  isDocumentationPopulated: () => {
    return get().existingRecordingState?.transcriptionStatus === 'completed';
  },

  /**
   * Save an edited transcript to the backend and update local state.
   */
  saveTranscriptEdit: async (newTranscript) => {
    const recordingId = get().recordingId;
    if (!recordingId) return;

    try {
      await apiService.updateRecordingTranscript(recordingId, newTranscript);
      scribeEncounterService.currentRecording.transcript = newTranscript;
      set({ transcript: newTranscript });
    } catch (error) {
      console.error('[ScribeStore] Failed to save transcript:', error);
      throw error;
    }
  },

  // ============ Transcription & Generation ============

  transcribeAudio: async (audioBlob) => {
    set({ isTranscribing: true, transcriptionError: null });

    try {
      const result = await aiService.transcribeAudio(audioBlob, {
        language: get().settings.language
      });

      set({
        transcript: result.text,
        isTranscribing: false
      });

      return result.text;
    } catch (error) {
      set({
        transcriptionError: error.message,
        isTranscribing: false
      });
      throw error;
    }
  },

  /**
   * Apply the /ai/transcribe-ehr response to store state (shared by the
   * direct-upload and legacy base64 paths).
   */
  _applyTranscribeResult: (result) => {
    // Endpoint returns immediately with { success: true, status: "processing" }
    // Realtime event will notify ClinicalNoteEdit when documentation is ready
    if (result?.success && result.status === 'processing') {
      set(state => ({
        processingStatus: 'generating',
        existingRecordingState: state.existingRecordingState ? {
          ...state.existingRecordingState,
          transcriptionStatus: 'processing'
        } : null,
      }));
    } else if (result?.sections) {
      // Fallback: if endpoint returns results synchronously
      set({
        generatedSectionsData: result,
        transcript: result.transcript || '',
        isProcessing: false,
        processingStatus: null,
      });
    } else {
      // Backend refused or errored synchronously — never strand the
      // spinner; surface the failure and leave the recording retryable.
      const message = result?.error || 'Transcription could not be started';
      set(state => ({
        isProcessing: false,
        processingStatus: null,
        noteGenerationError: message,
        existingRecordingState: state.existingRecordingState ? {
          ...state.existingRecordingState,
          transcriptionStatus: 'failed',
          transcriptionError: message,
        } : null,
      }));
    }
  },

  /**
   * Template-mode: send audio + template to /ai/transcribe-ehr for
   * transcription and section-based field extraction.
   */
  transcribeAndGenerate: async (encounterId) => {
    const { selectedTemplate, recordingId } = get();

    if (!selectedTemplate) {
      return { success: false, error: 'No template selected' };
    }

    set({ isProcessing: true, processingStatus: 'transcribing', noteGenerationError: null });

    try {
      // The backend requires a saved recording row — if the save failed
      // earlier (offline), retry it now so transcription can be anchored.
      let effectiveRecordingId = recordingId || scribeEncounterService.currentRecording?.backendId;
      if (!effectiveRecordingId && scribeEncounterService.currentRecording) {
        try {
          const saved = await scribeEncounterService.runSave();
          effectiveRecordingId = saved?.id || scribeEncounterService.currentRecording?.backendId;
        } catch { /* surfaced below */ }
      }
      if (!effectiveRecordingId) {
        throw new Error('Recording is not saved yet — check your connection and try again');
      }

      const { activeNoteContext } = get();
      const currentRecording = scribeEncounterService.currentRecording;

      // Direct-upload rows: the audio already sits in cloud storage and the
      // backend transcribes from the recording row, so the request carries no
      // audio at all. Any unsaved session must land first (the base64 path
      // could transcribe from memory; the object path cannot).
      if (currentRecording?.dirty && currentRecording.sessionBlob) {
        await scribeEncounterService.runSave();
      }
      if (currentRecording?.dirty && currentRecording.sessionBlob) {
        throw new Error('The latest audio has not finished saving — check your connection and try again');
      }

      if (scribeEncounterService.getAudioTransport() === 'object') {
        const objectPayload = {
          template: selectedTemplate,
          recording_id: effectiveRecordingId,
        };
        if (encounterId) objectPayload.encounter_id = encounterId;
        if (activeNoteContext?.noteId) objectPayload.clinical_note_id = activeNoteContext.noteId;

        const objectResult = await apiService.transcribeForEHR(objectPayload);
        get()._applyTranscribeResult(objectResult);
        return objectResult;
      }

      // Legacy (attachment) rows: ordered audio parts, one per recorder
      // session. The common case is a single in-memory session blob;
      // cross-session recordings download the other segment files. Audio is
      // never concatenated client-side.
      const audioParts = await scribeEncounterService.getSessionAudioParts();

      if (!audioParts.length) {
        throw new Error('No audio available for transcription');
      }

      // Convert blobs to base64 data URLs (Xano needs the full data URL
      // prefix e.g. "data:audio/webm;base64,..." for storage.create_file_resource)
      const toDataURL = (blob) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });

      const base64Segments = [];
      for (const part of audioParts) {
        base64Segments.push(await toDataURL(part));
      }

      // Determine file extension
      const mimeType = audioParts[0].type || 'audio/webm';
      const extension = mimeType.includes('mp4') || mimeType.includes('m4a') ? 'm4a' : 'webm';

      const payload = {
        // Legacy single-audio field kept so an older backend still works
        // (single-segment recordings — the common case — are identical);
        // the new backend prefers audio_segments when present.
        audio: base64Segments[0],
        audio_segments: base64Segments,
        filename: `recording_${effectiveRecordingId}.${extension}`,
        template: selectedTemplate,
        recording_id: effectiveRecordingId,
      };
      if (encounterId) payload.encounter_id = encounterId;
      if (activeNoteContext?.noteId) payload.clinical_note_id = activeNoteContext.noteId;

      const result = await apiService.transcribeForEHR(payload);
      get()._applyTranscribeResult(result);
      return result;
    } catch (error) {
      set({
        noteGenerationError: error.message,
        isProcessing: false,
        processingStatus: null,
      });
      throw error;
    }
  },

  /**
   * Legacy SOAP mode: generate note from transcript via streaming SSE
   */
  generateSOAPNote: async (patientContext = {}) => {
    const { transcript } = get();

    if (!transcript) {
      throw new Error('No transcript available');
    }

    set({
      isGeneratingNote: true,
      noteGenerationError: null,
      generatedNote: {
        subjective: { content: '' },
        objective: { content: '' },
        assessment: { content: '' },
        plan: { content: '' },
        icd10_suggestions: []
      }
    });

    try {
      const note = await aiService.generateSOAPNote(transcript, patientContext, {
        onProgress: (progress) => {
          const currentNote = get().generatedNote;
          if (progress.section && progress.content) {
            set({
              generatedNote: {
                ...currentNote,
                [progress.section]: {
                  content: currentNote[progress.section].content + progress.content + '\n'
                }
              }
            });
          }
        }
      });

      set({
        generatedNote: note,
        isGeneratingNote: false
      });

      return note;
    } catch (error) {
      set({
        noteGenerationError: error.message,
        isGeneratingNote: false
      });
      throw error;
    }
  },

  // ============ UI Actions ============

  clearTranscript: () => {
    set({ transcript: '', transcriptionError: null });
  },

  clearGeneratedNote: () => {
    set({ generatedNote: null, generatedSectionsData: null, noteGenerationError: null });
  },

  updateSettings: (newSettings) => {
    set({ settings: { ...get().settings, ...newSettings } });
  },

  toggleMinimized: () => {
    set({ isMinimized: !get().isMinimized });
  },

  toggleTranscript: () => {
    set({ showTranscript: !get().showTranscript });
  },

  reset: () => {
    scribeEncounterService.reset();
    set({
      isRecording: false,
      isPaused: false,
      recordingDuration: 0,
      recordingStartTime: null,
      wakeLockActive: false,
      encounterInitialized: false,
      recordingId: null,
      existingRecordingState: null,
      selectedTemplate: null,
      transcript: '',
      transcriptionError: null,
      generatedNote: null,
      generatedSectionsData: null,
      noteGenerationError: null,
      isProcessing: false,
      processingStatus: null,
      showTranscript: false,
    });
  }
}));
